/**
 * #mini-cookie-banner shows once the Cookie Information script has loaded
 * and asked for consent (its CookieInformationFormShown event).
 *
 * It never records consent itself: Accept/Reject click the real
 * #acceptButton / #declineButton (injected asynchronously by that script —
 * see cookie-information/cookie-info.html/.js/.css) so
 * CookieInformation.submitAllCategories() / declineAllCategories() run
 * exactly as they would from the default banner, when they exist.
 * The Preferences button reveals the original Cookie Information pop up (normally force-hidden by
 * mini-cookie-banner.css) so visitors can choose per-category consent
 * instead of a flat accept/reject.
 */
(function () {
  function hasConsentCookie() {
    return /(?:^|; )CookieInformationConsent=/.test(document.cookie);
  }

  function openPreferences(miniBanner) {
    var overlay = document.getElementById("coiOverlay");
    if (!overlay) return;
    miniBanner.hidden = true;
    /* mini-cookie-banner.css hides #coiOverlay with `!important`; an
     inline `!important` takes priority over that, so this is what
     actually makes the real banner visible again. */
    if (typeof showCookieBanner === "function") showCookieBanner();
    overlay.style.setProperty("display", "flex", "important");
    if (typeof toggleDetails === "function") toggleDetails("show_details");
  }

  function watchOverlayClose(miniBanner) {
    var overlay = document.getElementById("coiOverlay");
    if (!overlay) {
      var observer = new MutationObserver(function () {
        var found = document.getElementById("coiOverlay");
        if (found) {
          observer.disconnect();
          watchOverlayClose(miniBanner);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return;
    }

    new MutationObserver(function () {
      if (overlay.getAttribute("aria-hidden") === "true") {
        miniBanner.hidden = true;
        // Release the inline override from openPreferences() now that
        // the overlay is closing, so mini-cookie-banner.css's own
        // #coiOverlay { display: none !important } is back in control.
        overlay.style.removeProperty("display");
      }
    }).observe(overlay, { attributes: true, attributeFilter: ["aria-hidden"] });
  }

  function watchScrollLock(miniBanner) {
    function syncScrollLock() {
      document.documentElement.classList.toggle(
        "mini-cookie-banner-no-scroll",
        !miniBanner.hidden
      );
    }
    new MutationObserver(syncScrollLock).observe(miniBanner, {
      attributes: true,
      attributeFilter: ["hidden"],
    });
    syncScrollLock();
  }

  var FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  // Keeps keyboard focus cycling within the banner (Tab / Shift+Tab) while
  // it's open, matching the WAI-ARIA dialog pattern implied by its
  // role="dialog" aria-modal="true" and the backdrop that blocks the rest
  // of the page.
  function trapFocus(miniBanner) {
    miniBanner.addEventListener("keydown", function (event) {
      if (event.key !== "Tab") return;
      var focusables = miniBanner.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  // Makes the rest of the page fully unreachable — not just visually
  // behind the backdrop, but genuinely unfocusable and unclickable — while
  // the banner is open, using the native `inert` attribute on every other
  // direct child of <body>. This is what actually keeps Tab (from
  // anywhere, including the very first press on page load) landing inside
  // the banner instead of wandering into page content: trapFocus() below
  // only catches Tab once focus is already inside the dialog, which can't
  // stop it reaching the page in the first place on its own.
  function watchInertBackground(miniBanner, backdrop) {
    function syncInert() {
      var isOpen = !miniBanner.hidden;
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === miniBanner || el === backdrop) return;
        if (isOpen) {
          el.setAttribute("inert", "");
        } else {
          el.removeAttribute("inert");
        }
      });
    }
    new MutationObserver(syncInert).observe(miniBanner, {
      attributes: true,
      attributeFilter: ["hidden"],
    });
    syncInert();
  }

  // Moves focus into the banner when it appears (it blocks the rest of the
  // page behind a backdrop, so a keyboard user needs to land inside it
  // directly) and returns focus to whatever was focused before once it
  // closes again.
  function watchFocus(miniBanner) {
    var lastFocused = null;
    function syncFocus() {
      if (!miniBanner.hidden) {
        lastFocused = document.activeElement;
        var firstFocusable = miniBanner.querySelector(FOCUSABLE_SELECTOR);
        if (firstFocusable) firstFocusable.focus();
      } else if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
        lastFocused = null;
      }
    }
    new MutationObserver(syncFocus).observe(miniBanner, {
      attributes: true,
      attributeFilter: ["hidden"],
    });
    if (!miniBanner.hidden) syncFocus();
  }

  function init() {
    var miniBanner = document.getElementById("mini-cookie-banner");
    if (!miniBanner) return;
    var backdrop = document.getElementById("mini-cookie-banner-backdrop");

    miniBanner.hidden = true;
    watchScrollLock(miniBanner);
    watchInertBackground(miniBanner, backdrop);
    trapFocus(miniBanner);
    watchFocus(miniBanner);

    document
      .getElementById("mini-cookie-banner__accept")
      .addEventListener("click", function () {
        miniBanner.hidden = true;
        var acceptButton = document.getElementById("acceptButton");
        if (acceptButton) acceptButton.click();
      });
    document
      .getElementById("mini-cookie-banner__reject")
      .addEventListener("click", function () {
        miniBanner.hidden = true;
        var declineButton = document.getElementById("declineButton");
        if (declineButton) declineButton.click();
      });
    var miniPreferences = document.getElementById("preferences-btn");
    if (miniPreferences) {
      miniPreferences.addEventListener("click", function () {
        openPreferences(miniBanner);
      });
    }

    // The real overlay is injected asynchronously by the Cookie
    // Information script (and may never appear at all in an environment
    // that script doesn't run in, e.g. local dev) — sync a close from it
    // once it exists, without blocking this banner's own buttons on it.
    watchOverlayClose(miniBanner);
    showWhenCookieInformationReady(miniBanner);
  }

  // Shows the mini banner only once Cookie Information has loaded and asked
  // for consent, so Accept/Reject can always record it. wasBannerShown()
  // covers the event having fired before this script ran.
  function showWhenCookieInformationReady(miniBanner) {
    if (
      window.CookieInformation &&
      typeof window.CookieInformation.wasBannerShown === "function" &&
      window.CookieInformation.wasBannerShown()
    ) {
      miniBanner.hidden = false;
      return;
    }
    window.addEventListener(
      "CookieInformationFormShown",
      function () {
        miniBanner.hidden = false;
      },
      { once: true }
    );
  }

  init();
})();
